import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  BankIcon,
  CreditCardIcon,
  GiftIcon,
  HouseLineIcon,
  LockKeyIcon,
  NotePencilIcon,
  ReceiptIcon,
  TicketIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../../shared/ui';
import {
  paymentService,
  rewardService,
  userService,
  type DashboardStats,
  type Payment,
} from '../../../shared/utils';
import type { UserReward } from '../../../shared/utils/rewardTypes';
import { PaymentRow } from '../components/PaymentRow';
import { formatCouponValue } from '../utils/format';
import './Dashboard.css';

const RECENT_LIMIT = 3;

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [rewards, setRewards] = useState<UserReward[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    Promise.allSettled([
      userService.getDashboardStats(),
      paymentService.getPayments(),
      rewardService.getRewardHistory(),
    ]).then(([statsRes, paymentsRes, rewardsRes]) => {
      if (cancelled) return;
      if (statsRes.status === 'fulfilled' && statsRes.value.data) {
        setStats(statsRes.value.data);
      }
      if (paymentsRes.status === 'fulfilled' && paymentsRes.value.data) {
        setPayments(paymentsRes.value.data);
      }
      if (rewardsRes.status === 'fulfilled' && rewardsRes.value.data) {
        setRewards(rewardsRes.value.data);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const heading = (
    <div className="mc-heading">
      <div>
        <h1>{t('dashboard.welcomeName', { name: user?.firstName ?? '' })}</h1>
        <p>{t('dashboard.welcomeSub')}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        {heading}
        <section className="mc-card mc-hero dashboard__hero--loading" />
      </>
    );
  }

  const hasPaidRent = payments.some(p => p.status === 'EXECUTED');
  const recentPayments = payments.slice(0, RECENT_LIMIT);

  const recentPaymentsCard = (title: string) => (
    <section className="mc-card">
      <div className="mc-row">
        <h2>{title}</h2>
        <Link to="/payments" className="mc-link">
          {t('dashboard.viewAll')} <ArrowRightIcon size={14} />
        </Link>
      </div>
      <div className="mc-tablewrap">
        <table className="mc-table">
          <thead>
            <tr>
              <th>{t('app.table.date')}</th>
              <th>{t('app.table.recipient')}</th>
              <th>{t('app.table.amount')}</th>
              <th>{t('app.table.reference')}</th>
              <th>{t('app.table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {recentPayments.map(payment => (
              <PaymentRow key={payment.id} payment={payment} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  // No completed rent payment yet: guide the user to their first payment
  if (!hasPaidRent) {
    const steps = [
      {
        icon: <NotePencilIcon size={26} />,
        title: t('dashboard.step1Title'),
        text: t('dashboard.step1Text'),
      },
      {
        icon: <BankIcon size={26} />,
        title: t('dashboard.step2Title'),
        text: t('dashboard.step2Text'),
      },
      {
        icon: <GiftIcon size={26} />,
        title: t('dashboard.step3Title'),
        text: t('dashboard.step3Text'),
      },
    ];

    return (
      <>
        {heading}
        <section className="mc-card mc-hero">
          <div>
            <h2>{t('dashboard.firstTitle')}</h2>
            <p>{t('dashboard.firstText')}</p>
            <Link to="/pay-rent" className="mc-btn mc-btn--white">
              <CreditCardIcon size={20} weight="bold" />
              {t('dashboard.firstButton')}
            </Link>
          </div>
          <HouseLineIcon size={110} weight="duotone" className="mc-hero__art" />
        </section>

        <section className="mc-card">
          <h2>{t('dashboard.firstStepsTitle')}</h2>
          <div className="mc-grid mc-cols3">
            {steps.map((step, index) => (
              <div key={step.title} className="dashboard__step">
                <div className="dashboard__step-head">
                  <span className="dashboard__step-no">{index + 1}</span>
                  <span className="mc-metric__symbol">{step.icon}</span>
                </div>
                <h3>{step.title}</h3>
                <p className="mc-muted">{step.text}</p>
              </div>
            ))}
          </div>
          <div className="mc-callout dashboard__note">
            <strong>
              <LockKeyIcon size={18} weight="bold" />
              {t('dashboard.firstNoteTitle')}
            </strong>
            <p>{t('dashboard.firstNoteText')}</p>
          </div>
        </section>

        {recentPayments.length > 0 &&
          recentPaymentsCard(t('dashboard.inProgress'))}
      </>
    );
  }

  const latestReward =
    rewards.find(r => !r.isUsed && !r.isExpired) ?? rewards[0];

  const metrics = [
    {
      icon: <GiftIcon size={27} weight="duotone" />,
      value: stats?.rewardPoints ?? 0,
      label: t('dashboard.points'),
    },
    {
      icon: <ReceiptIcon size={27} weight="duotone" />,
      value: stats?.paymentCount ?? payments.length,
      label: t('dashboard.totalPayments'),
    },
    {
      icon: <TicketIcon size={27} weight="duotone" />,
      value: stats?.rewardCount ?? rewards.length,
      label: t('dashboard.rewardsClaimed'),
    },
  ];

  return (
    <>
      {heading}
      <section className="mc-card mc-hero">
        <div>
          <h2>{t('dashboard.heroTitle')}</h2>
          <p>{t('dashboard.heroText')}</p>
          <Link to="/pay-rent" className="mc-btn mc-btn--white">
            <CreditCardIcon size={20} weight="bold" />
            {t('dashboard.heroButton')}
          </Link>
        </div>
        <HouseLineIcon size={110} weight="duotone" className="mc-hero__art" />
      </section>

      <div className="mc-grid mc-cols3">
        {metrics.map(metric => (
          <section key={metric.label} className="mc-card">
            <div className="mc-metric">
              <span className="mc-metric__symbol">{metric.icon}</span>
              <div>
                <b>{metric.value}</b>
                <span>{metric.label}</span>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className="mc-grid mc-cols2">
        {recentPaymentsCard(t('dashboard.recentPayments'))}

        <section className="mc-card">
          <h2>{t('dashboard.yourRewards')}</h2>
          {latestReward?.coupon ? (
            <div className="mc-coupon">
              <div>
                <h3>{latestReward.coupon.name}</h3>
                <p>
                  {[
                    latestReward.coupon.merchantName,
                    latestReward.coupon.description,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <span className="mc-discount">
                  {formatCouponValue(latestReward.coupon, t)}
                </span>
              </div>
              <span
                className={`mc-tag ${
                  latestReward.isUsed || latestReward.isExpired
                    ? 'mc-tag--neutral'
                    : 'mc-tag--success'
                }`}
              >
                {latestReward.isExpired
                  ? t('rewardsHistory.expired')
                  : latestReward.isUsed
                    ? t('rewardsHistory.used')
                    : t('rewardsHistory.available')}
              </span>
            </div>
          ) : (
            <div className="mc-empty">{t('dashboard.noRewards')}</div>
          )}
          <div className="dashboard__rewards-action">
            <Link to="/rewards/history" className="mc-btn mc-btn--outline">
              {t('dashboard.viewRewardsButton')}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};
